import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Microscope, Brain, Upload, CheckCircle, Zap, Layers, MapPin, Database } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useAppStore } from '../store/useAppStore';

const LandingPage = () => {
  const user = useAppStore(state => state.user);
  const isAuthInitialized = useAppStore(state => state.isAuthInitialized);

  // If already logged in, skip landing page
  if (isAuthInitialized && user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="border-b border-slate-100 sticky top-0 bg-white/80 backdrop-blur-md z-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 h-18 flex items-center justify-between py-4">
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
            <Microscope className="h-8 w-8 text-primary-500" />
            <span className="ml-2 text-xl font-semibold text-slate-900 tracking-tight">HistologyPro</span>
          </Link>
          <div className="hidden sm:flex gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-sm font-bold uppercase tracking-widest text-slate-500">Sign In</Button>
            </Link>
            <Link to="/signup">
              <Button className="text-sm font-black uppercase tracking-widest px-6 rounded-xl">Get Started</Button>
            </Link>
          </div>
          <div className="sm:hidden">
             <Link to="/login">
               <Button size="sm" className="rounded-xl px-4 text-xs font-bold uppercase tracking-widest">Sign In</Button>
             </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section - SEO Optimized */}
      <div className="relative overflow-hidden pt-20 pb-24 md:pt-32 md:pb-40 px-6">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto space-y-8 md:space-y-10">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
              The Ultimate Histology <br className="hidden md:block" />
              <span className="text-primary-500">Active Recall Suite</span>
            </h1>
            
            <p className="text-lg md:text-2xl text-slate-600 max-w-2xl mx-auto leading-relaxed font-medium">
              Transform lecture PDFs into interactive study materials. Automatically extract slides, mark structures with anatomical arrows, and master histology with clinical-grade active recall.
            </p>

            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 pt-6 md:pt-10">
              <Link to="/signup" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto rounded-2xl px-10 h-14 md:h-16 text-sm font-black uppercase tracking-[0.2em] shadow-2xl shadow-primary-500/30">
                  Build My Bank
                </Button>
              </Link>
              <Link to="/upload" className="w-full sm:w-auto">
                <Button variant="outline" size="lg" className="w-full sm:w-auto rounded-2xl px-10 h-14 md:h-16 text-sm font-black uppercase tracking-[0.2em] border-slate-200 bg-white">
                  <Zap className="h-4 w-4 mr-2 text-amber-500 fill-amber-500" />
                  Try Instant Extraction
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Background blobs */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full -z-10 opacity-20 pointer-events-none">
            <div className="absolute top-20 left-1/4 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-[100px] animate-pulse"></div>
            <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-emerald-300 rounded-full mix-blend-multiply filter blur-[100px] animate-pulse delay-1000"></div>
        </div>
      </div>

      {/* Features Grid - Explains Value Proposition */}
      <div id="features" className="bg-slate-50 py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16 md:mb-20">
                <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight uppercase">Tailored for Med-Path Training</h2>
                <p className="text-slate-500 md:text-lg font-medium">Everything a medical student needs to master tissue identification.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {/* PDF processing */}
                <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 hover:shadow-xl transition-all group">
                    <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <Upload className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-black text-slate-900 mb-3 tracking-tight uppercase">PDF Processor</h3>
                    <p className="text-slate-600 leading-relaxed font-medium text-sm">Upload lecture PDFs and textbook sections to start extracting study data.</p>
                </div>
                {/* Slide Extraction */}
                <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 hover:shadow-xl transition-all group">
                    <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <Layers className="h-6 w-6 text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-black text-slate-900 mb-3 tracking-tight uppercase">Slide Extraction</h3>
                    <p className="text-slate-600 leading-relaxed font-medium text-sm">Our scanner isolates histology images at native resolution for clear viewing.</p>
                </div>
                {/* Anatomical Pins */}
                <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 hover:shadow-xl transition-all group">
                    <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <MapPin className="h-6 w-6 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-black text-slate-900 mb-3 tracking-tight uppercase">Clinical Arrows</h3>
                    <p className="text-slate-600 leading-relaxed font-medium text-sm">Mark specific cells or membranes to create custom quiz focal points.</p>
                </div>
                {/* Active Recall */}
                <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 hover:shadow-xl transition-all group">
                    <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <Brain className="h-6 w-6 text-amber-600" />
                    </div>
                    <h3 className="text-lg font-black text-slate-900 mb-3 tracking-tight uppercase">Quiz Generator</h3>
                    <p className="text-slate-600 leading-relaxed font-medium text-sm">Generate active recall sessions based on your own marked library.</p>
                </div>
            </div>
        </div>
      </div>

      {/* Trust Section */}
      <div className="py-24 px-6 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <div className="flex-1 space-y-6">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight uppercase">Clinically Precise Analysis</h2>
            <p className="text-slate-600 font-medium leading-relaxed">
              Designed specifically for the rigors of medical pathology, HistologyPro allows you to build a personal atlas from your university curriculum. No more generic study decks—practice on the exact material you will be tested on.
            </p>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-sm font-bold text-slate-700">
                <CheckCircle className="h-5 w-5 text-emerald-500" /> Professional-grade anatomical markers
              </li>
              <li className="flex items-center gap-3 text-sm font-bold text-slate-700">
                <CheckCircle className="h-5 w-5 text-emerald-500" /> AI-assisted tissue identification
              </li>
              <li className="flex items-center gap-3 text-sm font-bold text-slate-700">
                <CheckCircle className="h-5 w-5 text-emerald-500" /> Offline PWA support for hospital study
              </li>
            </ul>
          </div>
          <div className="flex-1 bg-slate-100 rounded-[3rem] p-4 aspect-square flex items-center justify-center relative overflow-hidden">
            <Database className="h-32 w-32 text-slate-200 absolute -bottom-8 -right-8" />
            <div className="relative z-10 bg-white p-8 rounded-[2rem] shadow-2xl border border-slate-100 max-w-xs text-center">
              <div className="bg-primary-50 p-4 rounded-2xl inline-block mb-6"><Microscope className="h-10 w-10 text-primary-500" /></div>
              <h4 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-2">Build Your Bank</h4>
              <p className="text-slate-400 text-xs font-bold leading-relaxed uppercase tracking-widest">Store thousands of slides across your academic career.</p>
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-white py-16 border-t border-slate-100 px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex items-center">
                <Microscope className="h-6 w-6 text-primary-500" />
                <span className="ml-2 text-lg font-semibold text-slate-900 tracking-tight">HistologyPro</span>
              </div>
              <div className="text-slate-400 text-xs font-bold uppercase tracking-widest">
                  &copy; 2025 Histology Practice Pro. All clinical rights reserved.
              </div>
              <div className="flex gap-6 text-xs font-black uppercase tracking-widest text-slate-600">
                  <a href="#" className="hover:text-primary-500">Privacy</a>
                  <a href="#" className="hover:text-primary-500">Terms</a>
                  <a href="#" className="hover:text-primary-500">Contact</a>
              </div>
          </div>
      </footer>
    </div>
  );
};

export default LandingPage;